'use strict';

import animations from 'tc-neos-animations';

const FADE_ITEM_CLASS = 'tc-fade-item';
const FADE_VISIBLE_CLASS = 'is-visible';
const FADE_IGNORE_SELECTOR = '[data-no-fade], script, style, link, meta, noscript, br';
const FOOTER_EXCLUDE_SELECTOR = 'footer, .footer, .footer-container, .footer-wrapper, .copyrights, .footer-menu-wrapper, .footer-content-wrapper';
const HEADER_MENU_EXCLUDE_SELECTOR = 'header, .header, .main-menu, .mmenu, .mm-menu, .mm-navbar, .mm-listview, .menu-wrapper, .navigation, .nav, .main-navigation';
const BUTTON_TEXT_EXCLUDE_SELECTOR = 'button, [role="button"], .btn, .tc-neos-elements-button, input[type="button"], input[type="submit"]';
const FADE_TEXT_SELECTOR = [
    'main h1', 'main h2', 'main h3', 'main h4', 'main h5', 'main h6',
    'main p',
    'main li',
    'main a',
    'main button',
    'main input[type="button"]',
    'main input[type="submit"]',
    'main [role="button"]',
    'main .tc-neos-elements-button',
    'main .tc-neos-elements-button-wrapper',
    'main span',
    'main blockquote',
    'main figcaption',
    'main dt',
    'main dd',
    'main label',
    // Ensure the counters cards also fade in as full elements.
    'main .tc-website-counters__item',
    'main .tc-website-counters__value',
    'main .tc-website-counters__label',
    // Logos strip items
    'main .tc-website-logos-strip__item',
    // Project detail sections
    'main .tc-website-project-detail__header',
    'main .tc-website-project-detail__media',
    // Default Neos image node type
    'main .neos-nodetypes-image',
    // LinkedIn feed widget (Elfsight)
    'main [class*="elfsight-app-"]'
].join(', ');

const isElementVisible = (element) => {
    if (!(element instanceof Element)) {
        return false;
    }

    const rect = element.getBoundingClientRect();
    if (!rect.width && !rect.height) {
        return false;
    }

    const styles = window.getComputedStyle(element);
    return styles.display !== 'none' && styles.visibility !== 'hidden' && styles.opacity !== '0';
};

const collectFadeTargets = () => {
    const candidates = document.querySelectorAll(FADE_TEXT_SELECTOR);
    return Array.from(candidates).filter((element) => {
        if (!(element instanceof Element)) {
            return false;
        }

        if (element.matches(FADE_IGNORE_SELECTOR)) {
            return false;
        }

        if (element.closest(FOOTER_EXCLUDE_SELECTOR)) {
            return false;
        }

        if (element.closest(HEADER_MENU_EXCLUDE_SELECTOR)) {
            return false;
        }

        // Projects list: animate the card link only, not the nested title.
        if (element.matches('.tc-website-projects-list__title')) {
            return false;
        }

        // Projects slider: animate the card link only, not the nested title.
        if (element.matches('.tc-website-projects-slider__project-title')) {
            return false;
        }

        // Keep fade on the button wrapper itself, but not on text nodes inside buttons.
        if (element.matches('span') && element.closest(BUTTON_TEXT_EXCLUDE_SELECTOR)) {
            return false;
        }

        if (!isElementVisible(element)) {
            return false;
        }

        // Animate only the first relevant container in a branch to avoid nested fade stacking.
        const animatedAncestor = element.parentElement?.closest(`.${FADE_ITEM_CLASS}`);
        return !animatedAncestor;
    });
};

const setupFadeIn = () => {
    const targets = collectFadeTargets();
    if (!targets.length) {
        return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
        targets.forEach((element) => element.classList.add(FADE_VISIBLE_CLASS));
        return;
    }

    targets.forEach((element) => {
        element.classList.add(FADE_ITEM_CLASS);
    });

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) {
                return;
            }

            entry.target.classList.add(FADE_VISIBLE_CLASS);
            observer.unobserve(entry.target);
        });
    }, {
        root: null,
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.08
    });

    const isInViewport = (element) => {
        const rect = element.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
        return rect.bottom > 0 && rect.right > 0 && rect.top < viewportHeight && rect.left < viewportWidth;
    };

    // Wait two frames so the hidden state is painted before any reveal.
    // Then reveal immediately visible elements with a small delay, and
    // observe the rest for scroll-based reveal.
    window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
            targets.forEach((element, index) => {
                if (isInViewport(element)) {
                    window.setTimeout(() => {
                        element.classList.add(FADE_VISIBLE_CLASS);
                    }, Math.min(index * 25, 250));
                    return;
                }

                observer.observe(element);
            });
        });
    });
};

const init = () => {
    setupFadeIn();
}

const scrollToTop = () => {
    animations.scrollToTop();
}

export default {init, scrollToTop}
